import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import type { ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Check, DropdownTriangle } from '@/assets/icons'
import { MenuContent, MenuItemGroup, MenuSeparator } from '@/components/menu/MenuContent'
import { getMenuItemClassName, getMenuItemContentClassNames } from '@/components/menu/menu-item-styles'

import { EditorPermissionsDescription } from './EditorPermissionsDescription'
import { kContributorRoleDescription } from './matter-member-copy'
import type { MatterAssignableRole } from './schemas'

/** Roles owners can assign to an invitation batch. */
const kInvitationRoles: { role: MatterAssignableRole; label: string; description: ReactNode }[] = [
  {
    role: 'collaborator',
    label: 'Contributor',
    description: kContributorRoleDescription,
  },
  {
    role: 'editor',
    label: 'Editor',
    description: <EditorPermissionsDescription />,
  },
]

interface InvitationRoleMenuProps {
  role: MatterAssignableRole
  onChange: (role: MatterAssignableRole) => void
  disabled: boolean
}

export function InvitationRoleMenu({ role, onChange, disabled }: InvitationRoleMenuProps) {
  const roleLabel = role === 'editor' ? 'Editor' : 'Contributor'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        aria-label={`Invitation role: ${roleLabel}`}
        disabled={disabled}
        className="flex shrink-0 items-center rounded-md bg-transparent py-1 pl-2 pr-0.5 text-sm font-medium leading-5 tracking-[-0.15px] text-brand-neutral-950 enabled:hover:bg-brand-neutral-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed data-[state=open]:bg-brand-neutral-100"
      >
        {roleLabel}
        <DropdownTriangle className="size-[18px] text-brand-neutral-600" aria-hidden />
      </DropdownMenu.Trigger>
      <MenuContent align="end" className="w-[265px] max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto">
        <DropdownMenu.RadioGroup
          aria-label="Invitation role"
          value={role}
          onValueChange={(value) => {
            if (value === 'collaborator' || value === 'editor') onChange(value)
          }}
        >
          <MenuItemGroup>
            {kInvitationRoles.map(({ role: value, label, description }) => {
              const { iconClassName, labelClassName, subheadingClassName } = getMenuItemContentClassNames({ variant: 'neutral' })

              return (
                <DropdownMenu.RadioItem key={value} value={value} className={getMenuItemClassName({ variant: 'neutral' })}>
                  <div className="min-w-0 flex-1">
                    <div className="flex min-h-6 items-center gap-2.5">
                      <span className={twMerge(labelClassName, 'min-w-0 flex-1')}>{label}</span>
                      <DropdownMenu.ItemIndicator>
                        <Check className={iconClassName} aria-hidden />
                      </DropdownMenu.ItemIndicator>
                    </div>
                    <p className={twMerge(subheadingClassName, 'whitespace-normal overflow-visible text-clip font-normal')}>{description}</p>
                  </div>
                </DropdownMenu.RadioItem>
              )
            })}
          </MenuItemGroup>
        </DropdownMenu.RadioGroup>
        <MenuSeparator />
        <div className="flex items-start gap-0.5 bg-brand-neutral-100 py-2 pl-3 pr-5 text-xs font-normal leading-4 text-brand-neutral-600">
          <span className="shrink-0 text-brand-800">*</span>
          <span>Editor access to billing summaries, drafts, and tables coming soon.</span>
        </div>
      </MenuContent>
    </DropdownMenu.Root>
  )
}
